import { useCallback, useEffect, useState } from "react";
import { Badge, Box, Button, Heading, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tooltip, Tr, VStack } from "@chakra-ui/react";
import BaseTemplate from "../components/BaseTemplate";
import InternalUserRoute from "../util/internal_user_route";
import { useBuildInfo } from "../hooks/useBuildInfo";
import { useApi } from "../hooks/useApi";
import { RemoteResource, RemoteResourceTest } from "../types";

function formatBuildTimestamp(iso: string | null): string {
  if (!iso) return "unknown";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "unknown";
  // YYYY-MM-DD HH:MM, viewer's local time zone
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Which backend this build talks to, read off the API origin the workflows bake in.
function environmentName(apiUrl: string): string {
  try {
    const host = new URL(apiUrl).host;
    if (host.startsWith("office-portal-backend-hosting-") || host === "api-office.eyeserve.co.uk") return "production";
    if (host.startsWith("api-office-development.")) return "development";
    if (host.startsWith("localhost") || host.startsWith("127.")) return "local";
    return host;
  } catch {
    return "unknown";
  }
}

// The build / deploy facts, as a two-column table matching the resource grid below it.
// Same layout as customer-portal's Technical Details page.
function DetailsTable({ rows }: { rows: [string, string][] }) {
  return (
    <TableContainer>
      <Table size="sm" variant="simple">
        <Thead>
          <Tr>
            <Th>Item</Th>
            <Th>Value</Th>
          </Tr>
        </Thead>
        <Tbody>
          {rows.map(([label, value]) => (
            <Tr key={label}>
              <Td fontWeight="bold" width="1%" whiteSpace="nowrap">{label}</Td>
              <Td fontFamily={value.startsWith("http") ? "mono" : undefined} whiteSpace="normal" wordBreak="break-all">
                {value}
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
}

type TestState = RemoteResourceTest | "testing";

// Every remote resource the backend is configured with (from its environment variables - see
// app/src/controllers/TechnicalDetails.ts), each tested on load and again on [Test]. Rendered
// inside InternalUserRoute, so it only mounts (and calls the staff-only routes) for staff.
function RemoteResourcesGrid() {
  const { ready, getRemoteResources, testRemoteResource } = useApi();
  const [resources, setResources] = useState<RemoteResource[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [tests, setTests] = useState<Record<string, TestState>>({});

  const runTest = useCallback(async (id: string) => {
    setTests((prev) => ({ ...prev, [id]: "testing" }));
    try {
      const result = await testRemoteResource(id);
      setTests((prev) => ({ ...prev, [id]: result }));
    } catch (error) {
      // the backend itself didn't answer - the resource's state is unknown, not proven down
      const message = error instanceof Error ? error.message : String(error);
      setTests((prev) => ({ ...prev, [id]: { id, status: "UNREACHABLE", ms: 0, error: `test call failed: ${message}` } }));
    }
  }, [testRemoteResource]);

  useEffect(() => {
    if (!ready) return;
    getRemoteResources()
      .then((list) => {
        // by type, then account, then alias
        setResources([...list].sort((a, b) =>
          a.type.localeCompare(b.type) || (a.account ?? "").localeCompare(b.account ?? "") || a.alias.localeCompare(b.alias)));
        list.forEach((r) => runTest(r.id));
      })
      .catch((error) => setLoadError(error instanceof Error ? error.message : String(error)));
  }, [ready, getRemoteResources, runTest]);

  if (loadError) return <Text color="red.500">Could not load remote resources: {loadError}</Text>;
  if (!resources) return <Text>loading…</Text>;

  return (
    <TableContainer>
      <Table size="sm" variant="simple">
        <Thead>
          <Tr>
            <Th>Re-test</Th>
            <Th>Status</Th>
            <Th>Type</Th>
            <Th>Account</Th>
            <Th>Alias</Th>
            <Th>Address</Th>
          </Tr>
        </Thead>
        <Tbody>
          {resources.map((r) => {
            const test = tests[r.id];
            const testing = test === undefined || test === "testing";
            return (
              <Tr key={r.id}>
                <Td>
                  <Button size="xs" onClick={() => runTest(r.id)} isLoading={testing}>
                    Test
                  </Button>
                </Td>
                <Td>
                  {testing ? (
                    <Badge>testing…</Badge>
                  ) : (
                    <Tooltip label={test.error ?? `${test.ms} ms`} hasArrow>
                      <Badge colorScheme={test.status === "OK" ? "green" : "red"}>{test.status}</Badge>
                    </Tooltip>
                  )}
                </Td>
                <Td>{r.type}</Td>
                <Td fontFamily="mono">{r.account ?? "—"}</Td>
                <Td>{r.alias}</Td>
                <Td fontFamily="mono" whiteSpace="normal" wordBreak="break-all">{r.address}</Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </TableContainer>
  );
}

export default function TechnicalDetails() {
  const { frontendBuiltAt, backendDeployedAt, backendCommit, backendStartedAt, isDevServer, loading } = useBuildInfo();
  const apiUrl = String(import.meta.env.VITE_API_URL ?? "");

  // A deployed backend has build-info.json; one run from source (`npm run dev`) only has its start time.
  const backendText = loading
    ? "checking…"
    : backendDeployedAt
      ? formatBuildTimestamp(backendDeployedAt) + (backendCommit ? ` (${backendCommit.slice(0, 7)})` : "")
      : backendStartedAt
        ? `${formatBuildTimestamp(backendStartedAt)} (not deployed - running from source, started then)`
        : "unknown";

  return (
    <InternalUserRoute>
      <BaseTemplate>
        <BaseTemplate.Header>
          <Heading size="lg">Technical Details</Heading>
          <Text color="gray.500" fontSize="sm">
            Which build is running, and whether the backend can reach what it depends on
          </Text>
        </BaseTemplate.Header>
        <BaseTemplate.Body>
          <VStack align="stretch" spacing={6} p={2}>
            <Box>
              <Text fontWeight="bold" mb={2}>Build and deploy</Text>
              <DetailsTable
                rows={[
                  ["Environment", environmentName(apiUrl)],
                  ["API", apiUrl || "unknown"],
                  ["Frontend built", formatBuildTimestamp(frontendBuiltAt) + (isDevServer && frontendBuiltAt ? " (Vite dev server - started then)" : "")],
                  ["Backend deployed", backendText],
                  ["Backend started", loading ? "checking…" : formatBuildTimestamp(backendStartedAt)],
                ]}
              />
            </Box>
            <Box>
              <Text fontWeight="bold" mb={2}>Remote resources</Text>
              <RemoteResourcesGrid />
            </Box>
          </VStack>
        </BaseTemplate.Body>
      </BaseTemplate>
    </InternalUserRoute>
  );
}
